import React, { useState, useEffect, useRef, useCallback } from "react";
import { Maximize2 } from "lucide-react";
import { API_BASE } from "../../config/api";
import { getGameDetails } from "../../utils/gameDetailsCache";
import { isUserWinnerForGame } from "../../utils/chartUtils";
import { svgElementToPngFile } from "../../utils/chartSnapshot";
import { filterTimelineByRange } from "../../utils/timelineRangeFilter";
import logger from '../../utils/logger';

// --- Pure layout helpers -----------------------------------------------
// These helpers contain no component state; they operate purely on the
// data passed to them so they can be reasoned about (and tested) in
// isolation from React rendering concerns.

const BUBBLE_FILL_RATIO = 0.85; // fallback for --activity-bubble-fill-ratio
const MIN_BUBBLE_EXTENT = 30; // fallback for --activity-bubble-min-extent (px)
const MIN_BUBBLE_RADIUS = 3; // fallback for --activity-bubble-min-radius (px)
const AXIS_RESERVED_RATIO = 0.15; // fallback for --activity-axis-reserved-ratio

/**
 * Scales bubble radii (mutating the bar objects in place) so that the day
 * with the largest total stack fills the available stacking axis length,
 * while individual bubble diameters never exceed `crossAxisSize * BUBBLE_FILL_RATIO`.
 * Works identically for the horizontal-stacking (vertical layout) case and the
 * vertical-stacking (horizontal layout) case - the caller decides what
 * "availableExtent" and "crossAxisSize" mean along their axis of choice.
 */
function applyBubbleStackScaling(barsByDay, maxDayPlaytime, availableExtent, crossAxisSize) {
  const maxAllowedDiameter = crossAxisSize * BUBBLE_FILL_RATIO;

  const dayGroups = Object.values(barsByDay);

  dayGroups.forEach((dayBars) => {
    dayBars.sort((a, b) => b.playtime - a.playtime);
    dayBars.forEach((bar) => {
      const calculatedExtent = maxDayPlaytime > 0 ? (bar.playtime / maxDayPlaytime) * availableExtent : 0;
      const barExtent = Math.max(MIN_BUBBLE_EXTENT, calculatedExtent);
      const radius = Math.max(MIN_BUBBLE_RADIUS, Math.min(maxAllowedDiameter / 2, barExtent / 2));
      bar.bubbleRadius = radius;
      bar.stackExtent = radius * 2;
    });
  });

  const stackExtentOf = (dayBars) => dayBars.reduce((sum, bar) => sum + bar.stackExtent, 0);
  const maxStackExtent = dayGroups.reduce((max, dayBars) => Math.max(max, stackExtentOf(dayBars)), 0);
  const scaleFactor = maxStackExtent > 0 ? availableExtent / maxStackExtent : 1;

  dayGroups.forEach((dayBars) => {
    dayBars.forEach((bar) => {
      const scaledRadius = Math.max(MIN_BUBBLE_RADIUS, bar.bubbleRadius * scaleFactor);
      bar.bubbleRadius = Math.min(scaledRadius, maxAllowedDiameter / 2);
      bar.stackExtent = bar.bubbleRadius * 2;
    });
  });

  return dayGroups.reduce((max, dayBars) => Math.max(max, stackExtentOf(dayBars)), 0);
}

/** Vertical layout: days on the Y-axis, bubbles stacked horizontally from the axis line. */
function positionBarsVertically(barsByDay, daysWithSessions, daySize, axisX, offset = 0) {
  daysWithSessions.forEach((dayEntry, dayIndex) => {
    const dayBars = barsByDay[dayEntry.date] || [];
    const center = dayIndex * daySize + daySize / 2 + offset;
    let cursor = axisX;
    dayBars.forEach((bar) => {
      bar.cx = cursor + bar.bubbleRadius;
      bar.cy = center;
      cursor += bar.stackExtent;
    });
  });
}

/** Horizontal layout: days on the X-axis, bubbles stacked vertically up from the axis line. */
function positionBarsHorizontally(barsByDay, daysWithSessions, daySize, axisY, offset = 0) {
  daysWithSessions.forEach((dayEntry, dayIndex) => {
    const dayBars = barsByDay[dayEntry.date] || [];
    const center = dayIndex * daySize + daySize / 2 + offset;
    let cursor = axisY;
    dayBars.forEach((bar) => {
      cursor -= bar.stackExtent;
      bar.cy = cursor + bar.bubbleRadius;
      bar.cx = center;
    });
  });
}

/** Builds the { date, displayDate, center } list used for axis grid lines and date labels. */
function buildDayPositions(daysWithSessions, daySize, offset = 0) {
  return daysWithSessions.map((dayEntry, index) => ({
    date: dayEntry.date,
    displayDate: dayEntry.display_date,
    center: index * daySize + daySize / 2 + offset
  }));
}

const ActivityBubbleChart = ({ timelineData, chartRange, rangeMode = 'sessions', onShare, shareStatus, onExpand, user, isFullscreen = false, shareTrigger = 0 }) => {
  const clipNs = React.useId().replace(/:/g, '');
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, game: null, day: null, playtime: 0 });
  const chartRef = useRef(null);
  const svgRef = useRef(null);
  const scrollContainerRef = useRef(null);
  // Fixed size per day (row height in vertical mode, column width in horizontal mode)
  const [dimensions, setDimensions] = useState({ width: typeof window !== 'undefined' ? Math.min(window.innerWidth - 32, 800) : 800, dayHeight: 80 });
  const [bubbleData, setBubbleData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scrollMode, setScrollMode] = useState('horizontal'); // 'horizontal' (default) or 'vertical'
  const DAYS_PER_VIEWPORT = 5;
  // Layout metadata describing axis position, svg size and per-day label anchors.
  // Recomputed alongside bubbleData whenever the data, dimensions or scrollMode change.
  const [layout, setLayout] = useState({
    mode: 'vertical',
    svgWidth: dimensions.width,
    svgHeight: DAYS_PER_VIEWPORT * dimensions.dayHeight,
    axisFixed: dimensions.width * AXIS_RESERVED_RATIO,
    daySize: dimensions.dayHeight,
    dayPositions: []
  });

  // Responsive date formatting function
  const formatDisplayDate = (dateString, allDates) => {
    if (!dateString) return '';

    // Extract date components from long format like "Saturday, April 25, 2026"
    const dateMatch = dateString.match(/([A-Za-z]+)\s+(\d+),\s*(\d{4})$/);
    if (!dateMatch) return dateString;

    const [, monthName, day, year] = dateMatch;

    // Convert month name to number
    const monthMap = {
      'January': '01', 'February': '02', 'March': '03', 'April': '04',
      'May': '05', 'June': '06', 'July': '07', 'August': '08',
      'September': '09', 'October': '10', 'November': '11', 'December': '12'
    };

    const monthNum = monthMap[monthName] || '01';
    const dayNum = day.padStart(2, '0');
    const yearShort = year.slice(-2);

    // Extract years from all dates to check if same year
    const allYears = [...new Set(allDates.map(date => {
      const match = date.match(/,\s*(\d{4})$/);
      return match ? match[1] : null;
    }))].filter(Boolean);

    if (allYears.length === 1) {
      // All dates have same year, use DD.MM format
      return `${dayNum}.${monthNum}`;
    }

    // Different years, use DD.MM.YY format
    return `${dayNum}.${monthNum}.${yearShort}`;
  };

  // Mobile-aware responsive dimensions for transposed layout. Dimensions only
  // update when they actually change, and resizes are debounced so tab switches,
  // fullscreen transitions and share actions don't repeatedly recalculate layout.
  useEffect(() => {
    const computeSize = (width) => {
      if (width === 0) {
        width = Math.min(window.innerWidth - 32, 800);
      }
      let dayHeight;
      if (window.innerWidth <= 480) {
        // Mobile: slightly smaller day height
        dayHeight = 70;
      } else if (window.innerWidth <= 768) {
        // Tablet
        dayHeight = 75;
      } else {
        // Desktop
        dayHeight = 80;
      }
      return { width, dayHeight };
    };

    const setIfChanged = (next) => {
      setDimensions((prev) => {
        if (prev.width === next.width && prev.dayHeight === next.dayHeight) {
          return prev;
        }
        return next;
      });
    };

    let resizeTimer;
    const updateDimensions = () => {
      const container = chartRef.current?.parentElement;
      if (!container) return;
      const width = container.clientWidth;
      setIfChanged(computeSize(width));
    };

    const debouncedUpdate = () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateDimensions, 150);
    };

    // Delay initial measurement to ensure DOM layout is settled
    const rafId = window.requestAnimationFrame(updateDimensions);

    window.addEventListener('resize', debouncedUpdate);

    // Use ResizeObserver to catch container size changes
    const container = chartRef.current?.parentElement;
    let resizeObserver;
    if (container && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(debouncedUpdate);
      resizeObserver.observe(container);
    }

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', debouncedUpdate);
      if (resizeTimer) clearTimeout(resizeTimer);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  // Calculate activity bubble data with daily aggregation
  const calculateActivityBubbleData = useCallback(async () => {
    setLoading(true);
    const gameDetailsCache = new Map();

    // Filter timeline data based on the last N sessions or last N days
    const filteredData = filterTimelineByRange(timelineData, chartRange, rangeMode);

    // Collect unique game IDs across all days
    const uniqueGameIds = [...new Set(filteredData.flatMap(dayEntry =>
      dayEntry.sessions ? dayEntry.sessions.map(s => s.game_id).filter(Boolean) : []
    ))];

    // Fetch game details for all unique games in parallel, reusing the shared
    // process-wide cache so tab switches / fullscreen / share do not re-request
    // the same details.
    await Promise.all(uniqueGameIds.map(async (gameId) => {
      const details = await getGameDetails(gameId);
      if (details) {
        gameDetailsCache.set(gameId, details);
      }
    }));

    // Normalize day entries so they always have a display_date (shared charts may only send `date`)
    const normalizedData = filteredData.map((dayEntry) => ({
      ...dayEntry,
      display_date:
        dayEntry.display_date ||
        new Date(dayEntry.date).toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }),
    }));

    // Create daily activity bars
    const activityBars = [];
    const daysWithSessions = normalizedData.filter(d => d.sessions && d.sessions.length > 0);

    daysWithSessions.forEach((dayEntry) => {
      // Calculate total playtime for this day
      let dayTotalPlaytime = 0;
      const gameSessions = new Map();

      dayEntry.sessions.forEach(session => {
        const gameId = session.game_id;
        const gameName = session.game_name || 'Unknown Game';
        const gameImage = gameId ? `${API_BASE}/games/${gameId}/thumbnail` : null;
        const playtime = session.playtime || 0;

        if (!gameSessions.has(gameId)) {
          gameSessions.set(gameId, {
            id: gameId,
            name: gameName,
            image: gameImage,
            playtime: 0,
            sessions: 0
          });
        }

        const gameSession = gameSessions.get(gameId);
        gameSession.playtime += playtime;
        gameSession.sessions += 1;
        dayTotalPlaytime += playtime;
      });

      // Use safer iteration method to avoid TypeError
      if (gameSessions && gameSessions instanceof Map) {
        try {
          for (const [gameId, gameSession] of gameSessions.entries()) {
            activityBars.push({
              id: `${dayEntry.date}-${gameId}`,
              gameId: gameId,
              gameName: gameSession.name,
              image: gameSession.image,
              date: dayEntry.date,
              displayDate: dayEntry.display_date,
              playtime: (() => {
          // Calculate playtime for this specific game using game details
          let playtime = 60; // Default fallback
          try {
            const gameDetails = gameDetailsCache.get(gameId);
            if (gameDetails) {
              const minPlaytime = gameDetails.min_playtime;
              const maxPlaytime = gameDetails.max_playtime;
              const avgPlaytime = gameDetails.avg_playtime || gameDetails.playing_time;

              if (minPlaytime && maxPlaytime) {
                playtime = (minPlaytime + maxPlaytime) / 2;
              } else if (avgPlaytime) {
                playtime = avgPlaytime;
              }
            }
          } catch (error) {
            logger.error('Error getting game details for tooltip:', error);
          }
          return playtime * gameSession.sessions; // Multiply by session count
        })(),
              sessions: gameSession.sessions,
              dayTotalPlaytime: dayTotalPlaytime // Use calculated total playtime
              // cx/cy/bubbleRadius are computed below once we know the layout mode
            });
          }
        } catch (error) {
          logger.error('Error iterating gameSessions:', error);
        }
      } else {
        logger.error('gameSessions is not a Map:', gameSessions);
      }
    });

    // Calculate total playtime per day using proper playtime from game details
    const dayPlaytimes = filteredData.map((d) => {
      // Calculate total playtime for this day from sessions using game details
      if (!Array.isArray(d.sessions)) {
        return 1;
      }

      const total = d.sessions.reduce((sum, session) => {
        let playtime = 60; // Default fallback

        // Get game details from cache like BubbleChart does
        try {
          const gameDetails = gameDetailsCache.get(session.game_id);

          if (gameDetails) {
            const minPlaytime = gameDetails.min_playtime;
            const maxPlaytime = gameDetails.max_playtime;
            const avgPlaytime = gameDetails.avg_playtime || gameDetails.playing_time;

            if (minPlaytime && maxPlaytime) {
              playtime = (minPlaytime + maxPlaytime) / 2;
            } else if (avgPlaytime) {
              playtime = avgPlaytime;
            }
          }
        } catch (error) {
          logger.error('Error getting game details:', error);
        }

        return sum + playtime;
      }, 0);

      return total || 1; // Ensure at least 1 to avoid division by zero
    });
    const maxDayPlaytime = Math.max(...dayPlaytimes);

    // Group bars by day for stacking
    const barsByDay = {};
    activityBars.forEach(bar => {
      if (!barsByDay[bar.date]) {
        barsByDay[bar.date] = [];
      }
      barsByDay[bar.date].push(bar);
    });

    // daySize is reused as the row height in vertical mode and the column
    // width in horizontal mode, so both layouts stay visually consistent.
    const daySize = dimensions.dayHeight;
    const numDays = daysWithSessions.length;
    const isHorizontal = scrollMode === 'horizontal';

    let nextLayout;

    if (isHorizontal) {
      // Days on the X-axis, bubbles stacked vertically (bottom-aligned) per day column.
      const plotHeight = Math.max(DAYS_PER_VIEWPORT * daySize, daySize * 3);
      const bottomPadding = plotHeight * AXIS_RESERVED_RATIO; // reserved for date labels below the axis
      const bubbleAreaHeight = plotHeight - bottomPadding;
      const axisY = bubbleAreaHeight;

      // Center the day columns horizontally when there are fewer days than the viewport width.
      const contentWidth = numDays * daySize;
      const hOffset = Math.max(0, (dimensions.width - contentWidth) / 2);

      applyBubbleStackScaling(barsByDay, maxDayPlaytime, bubbleAreaHeight, daySize);
      positionBarsHorizontally(barsByDay, daysWithSessions, daySize, axisY, hOffset);

      nextLayout = {
        mode: 'horizontal',
        svgWidth: Math.max(dimensions.width, contentWidth),
        svgHeight: plotHeight,
        axisFixed: axisY,
        daySize,
        dayPositions: buildDayPositions(daysWithSessions, daySize, hOffset)
      };
    } else {
      // Days on the Y-axis, bubbles stacked horizontally (left-aligned) per day row.
      const leftPadding = dimensions.width * AXIS_RESERVED_RATIO; // reserved for Y-axis + date labels
      const chartWidth = dimensions.width - leftPadding;
      const axisX = leftPadding;

      // Center the day rows vertically when there are fewer days than the viewport height.
      const viewportHeight = DAYS_PER_VIEWPORT * daySize;
      const contentHeight = numDays * daySize;
      const vOffset = Math.max(0, (viewportHeight - contentHeight) / 2);

      applyBubbleStackScaling(barsByDay, maxDayPlaytime, chartWidth, daySize);
      positionBarsVertically(barsByDay, daysWithSessions, daySize, axisX, vOffset);

      nextLayout = {
        mode: 'vertical',
        svgWidth: dimensions.width,
        svgHeight: Math.max(viewportHeight, contentHeight),
        axisFixed: axisX,
        daySize,
        dayPositions: buildDayPositions(daysWithSessions, daySize, vOffset)
      };
    }

    setBubbleData(activityBars);
    setLayout(nextLayout);
    setLoading(false);
  }, [timelineData, chartRange, rangeMode, dimensions, scrollMode]);

  useEffect(() => {
    calculateActivityBubbleData();
  }, [calculateActivityBubbleData]);

  // Compute the bounding box of all placed bubbles (plus a glow margin)
  // so the exported PNG can be cropped to the actual chart content.
  const calculateCropBounds = useCallback(() => {
    if (!bubbleData.length) return null;
    const margin = 5;
    const minX = Math.min(...bubbleData.map((b) => b.cx - b.bubbleRadius)) - margin;
    const maxX = Math.max(...bubbleData.map((b) => b.cx + b.bubbleRadius)) + margin;
    const minY = Math.min(...bubbleData.map((b) => b.cy - b.bubbleRadius)) - margin;
    const maxY = Math.max(...bubbleData.map((b) => b.cy + b.bubbleRadius)) + margin;
    return {
      x: Math.max(0, minX),
      y: Math.max(0, minY),
      width: Math.min(layout.svgWidth, maxX) - Math.max(0, minX),
      height: Math.min(layout.svgHeight, maxY) - Math.max(0, minY),
    };
  }, [bubbleData, layout.svgWidth, layout.svgHeight]);

  // Share handler - only works if onShare prop is provided
  const handleShare = useCallback(async () => {
    if (!chartRef.current || !onShare) return;

    try {
      const svgElement = svgRef.current;
      if (!svgElement) return;

      const file = await svgElementToPngFile(svgElement, {
        width: layout.svgWidth,
        height: layout.svgHeight,
        cropBounds: calculateCropBounds(),
        filename: 'activity-bubble-chart.png',
      });

      if (file) {
        onShare(file);
      }
    } catch (error) {
      logger.error('Error sharing chart:', error);
    }
  }, [onShare, layout.svgWidth, layout.svgHeight, calculateCropBounds]);

  // Allow the fullscreen modal header to trigger chart image sharing.
  // The trigger is consumed only once per increment so a parent re-render
  // cannot reopen the share sheet after it has been closed.
  const lastShareTriggerRef = useRef(0);
  useEffect(() => {
    if (shareTrigger > 0 && shareTrigger !== lastShareTriggerRef.current) {
      lastShareTriggerRef.current = shareTrigger;
      handleShare();
    }
  }, [shareTrigger, handleShare]);

  return (
    <div className="chart-card" style={{
      backgroundColor: "var(--bg-secondary, #fafbfc)",
      borderRadius: "12px",
      padding: isFullscreen ? 0 : "clamp(0.5rem, 1.5vw, 1rem)",
      position: "relative",
      overflow: "hidden",
      display: isFullscreen ? "flex" : undefined,
      flexDirection: isFullscreen ? "column" : undefined,
      height: isFullscreen ? "100%" : undefined
    }}>
      {/* Chart Header - hidden in fullscreen so only the chart area is shown */}
      {!isFullscreen && (
      <div style={{
        marginBottom: "1rem",
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "1rem",
        backgroundColor: "var(--bg-secondary, white)",
        padding: "0.75rem",
        borderRadius: "8px"
      }}>
        {/* Share Button - only shown if onShare prop is provided */}
        {onShare && (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            {shareStatus && (
              <span style={{
                fontSize: "clamp(0.75rem, 2vw, 0.875rem)",
                color: "var(--pista-green-primary, #2E7D32)",
                fontWeight: "600"
              }}>
                {shareStatus}
              </span>
            )}

            <button
              onClick={handleShare}
              style={{
                padding: "clamp(0.4rem, 1.5vw, 0.55rem) clamp(0.5rem, 2vw, 0.75rem)",
                borderRadius: "8px",
                border: "1px solid var(--pista-green-secondary, #66BB6A)",
                backgroundColor: "var(--pista-green-primary, #2E7D32)",
                color: "var(--pista-button-text, white)",
                cursor: "pointer",
                fontWeight: "600",
                fontSize: "clamp(0.75rem, 2vw, 0.875rem)",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                transition: "all 0.2s ease"
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = "var(--pista-green-hover, #388E3C)";
                e.target.style.color = "var(--pista-button-text, #FFFFFF)";
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = "var(--pista-green-primary, #2E7D32)";
                e.target.style.color = "var(--pista-button-text, white)";
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                style={{ flexShrink: 0 }}
              >
                <path
                  d="M18 8C19.6569 8 21 6.65685 21 5C21 3.34315 19.6569 2 18 2C16.3431 2 15 3.34315 15 5C15 5.12548 15.0076 5.24886 15.0224 5.36983L9.06253 8.36983C8.68487 7.56027 7.86668 7 6.92857 7C5.54811 7 4.42857 8.11954 4.42857 9.5C4.42857 10.8805 5.54811 12 6.92857 12C7.86668 12 8.68487 11.4397 9.06253 10.6302L15.0224 13.6302C15.0076 13.7511 15 13.8745 15 14C15 15.6569 16.3431 17 18 17C19.6569 17 21 15.6569 21 14C21 12.3431 19.6569 11 18 11C17.0619 11 16.2437 11.5603 15.866 12.3698L9.90616 9.36983C9.92097 9.24886 9.92857 9.12548 9.92857 9C9.92857 8.87452 9.92097 8.75114 9.90616 8.63017L15.866 5.63017C16.2437 6.43973 17.0619 7 18 7Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Expand/Fullscreen Button - only shown if onExpand prop is provided */}
        {onExpand && (
          <button
            onClick={onExpand}
            title="View fullscreen"
            aria-label="View fullscreen"
            style={{
              padding: "clamp(0.4rem, 1.5vw, 0.55rem)",
              borderRadius: "8px",
              border: "1px solid var(--border-color, #ddd)",
              backgroundColor: "var(--bg-primary, white)",
              color: "var(--text-primary, #333)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Maximize2 size={16} />
          </button>
        )}
      </div>
      )}

      {/* Activity Bubble Chart - Transposed with scroll toggle */}
      <div ref={chartRef} style={{
        position: "relative",
        flex: isFullscreen ? 1 : undefined,
        height: isFullscreen ? "100%" : undefined,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: isFullscreen ? "center" : undefined
      }}>
        {/* Scroll direction toggle - hidden in fullscreen */}
      {!isFullscreen && (
        <div style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "0.5rem"
        }}>
          <div
            role="group"
            aria-label="Scroll direction toggle"
            style={{
              display: "inline-flex",
              alignItems: "center",
              border: "1px solid var(--border-color, #ddd)",
              borderRadius: "8px",
              backgroundColor: "var(--bg-primary, white)",
              padding: "1px"
            }}
          >
            <button
              type="button"
              aria-pressed={scrollMode === 'horizontal'}
              onClick={() => setScrollMode('horizontal')}
              style={{
                padding: "0.25rem 0.75rem",
                borderRadius: "7px",
                border: "none",
                backgroundColor: scrollMode === 'horizontal' ? 'var(--pista-green-primary, #2E7D32)' : 'transparent',
                color: scrollMode === 'horizontal' ? 'white' : 'var(--text-secondary, #666)',
                fontSize: "0.75rem",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.2s ease",
                whiteSpace: "nowrap"
              }}
            >
              ↔ Horizontal
            </button>
            <button
              type="button"
              aria-pressed={scrollMode === 'vertical'}
              onClick={() => setScrollMode('vertical')}
              style={{
                padding: "0.25rem 0.75rem",
                borderRadius: "7px",
                border: "none",
                backgroundColor: scrollMode === 'vertical' ? 'var(--pista-green-primary, #2E7D32)' : 'transparent',
                color: scrollMode === 'vertical' ? 'white' : 'var(--text-secondary, #666)',
                fontSize: "0.75rem",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.2s ease",
                whiteSpace: "nowrap"
              }}
            >
              ↕ Vertical
            </button>
          </div>
        </div>
      )}

      {/* Scrollable container for days */}
      <div
        ref={scrollContainerRef}
        style={scrollMode === 'vertical' ? {
          position: "relative",
          maxHeight: `${DAYS_PER_VIEWPORT * layout.daySize}px`,
          overflowY: layout.dayPositions.length > DAYS_PER_VIEWPORT ? 'auto' : 'hidden',
          overflowX: 'hidden',
          border: "1px solid var(--border-color, #ddd)",
          borderRadius: "8px"
        } : {
          position: "relative",
          maxWidth: "100%",
          overflowX: 'auto',
          overflowY: 'hidden',
          border: "1px solid var(--border-color, #ddd)",
          borderRadius: "8px"
        }}
      >
        <div style={{ position: "relative", margin: "0 auto", width: "fit-content" }}>
          <svg
          ref={svgRef}
          width={layout.svgWidth}
          height={layout.svgHeight}
          style={{ display: "block" }}
        >
          {/* Background */}
          <rect
            width={layout.svgWidth}
            height={layout.svgHeight}
            fill="var(--bg-secondary, #fafbfc)"
          />

            {/* Axis line: Y-axis (vertical mode) or X-axis (horizontal mode) */}
            {scrollMode === 'vertical' ? (
              <line
                x1={layout.axisFixed}
                y1="0"
                x2={layout.axisFixed}
                y2={layout.svgHeight}
                stroke="var(--text-primary, #333)"
                strokeWidth="2"
              />
            ) : (
              <line
                x1="0"
                y1={layout.axisFixed}
                x2={layout.svgWidth}
                y2={layout.axisFixed}
                stroke="var(--text-primary, #333)"
                strokeWidth="2"
              />
            )}

            {/* Day grid lines: horizontal rows (vertical mode) or vertical columns (horizontal mode) */}
            {layout.dayPositions.map((dayPos, index) => (
              scrollMode === 'vertical' ? (
                <line
                  key={`day-line-${index}`}
                  x1="0"
                  y1={dayPos.center}
                  x2={layout.svgWidth}
                  y2={dayPos.center}
                  stroke="var(--border-color, #ddd)"
                  strokeWidth="1"
                  strokeDasharray="2,2"
                  opacity="0.3"
                />
              ) : (
                <line
                  key={`day-line-${index}`}
                  x1={dayPos.center}
                  y1="0"
                  x2={dayPos.center}
                  y2={layout.svgHeight}
                  stroke="var(--border-color, #ddd)"
                  strokeWidth="1"
                  strokeDasharray="2,2"
                  opacity="0.3"
                />
              )
            ))}

            {/* Stacked Activity Bubbles */}
            {bubbleData.map((bar) => {
              const centerY = bar.cy;
              const radius = bar.bubbleRadius || Math.max(MIN_BUBBLE_RADIUS, Math.min((layout.daySize * BUBBLE_FILL_RATIO) / 2, 30));
              const centerX = bar.cx;

              return (
                <g key={bar.id}>
                  {/* Bubble circle */}
                  <circle
                    cx={centerX}
                    cy={centerY}
                    r={radius}
                    fill="var(--pista-green-primary, #2E7D32)"
                    opacity={0.8}
                    stroke={isUserWinnerForGame(bar.gameId, user, timelineData) ? "#FFD700" : "var(--pista-green-secondary, #66BB6A)"}
                    strokeWidth={isUserWinnerForGame(bar.gameId, user, timelineData) ? "3" : "1"}
                    style={{
                      cursor: "pointer",
                      transform: "none",
                      transition: "none",
                      filter: isUserWinnerForGame(bar.gameId, user, timelineData) ? "drop-shadow(0 0 6px rgba(255, 215, 0, 0.6))" : "none"
                    }}
                    onMouseEnter={(_e) => {
                      setTooltip({
                        show: true,
                        x: centerX,
                        y: centerY - radius - 10,
                        game: bar,
                        day: bar.displayDate,
                        playtime: bar.playtime
                      });
                    }}
                    onMouseLeave={() => {
                      setTooltip({ show: false });
                    }}
                  />

                  {/* Game image with circular clipping - clipped to the
                      inner edge of the bubble stroke so the cover fills most of the circle.
                      The clip-path id is namespaced so multiple chart instances (e.g.
                      the page and fullscreen modal) never share an id. */}
                  {bar.gameId && (
                    <defs>
                      <clipPath id={`${clipNs}-clip-${bar.id}`}>
                        <circle
                          cx={centerX}
                          cy={centerY}
                          r={Math.max(1, radius - 1)}
                        />
                      </clipPath>
                    </defs>
                  )}

                  {bar.gameId && bar.image && (
                    <image
                      href={bar.image}
                      x={centerX - Math.max(1, radius - 1)}
                      y={centerY - Math.max(1, radius - 1)}
                      width={Math.max(2, (radius - 1) * 2)}
                      height={Math.max(2, (radius - 1) * 2)}
                      clipPath={`url(#${clipNs}-clip-${bar.id})`}
                      style={{
                        pointerEvents: "none",
                        opacity: 0.9,
                        transform: "none",
                        transition: "none"
                      }}
                      preserveAspectRatio="xMidYMid slice"
                    />
                  )}
                </g>
              );
            })}
          </svg>

          {/* Date Labels: right-aligned next to the Y-axis (vertical mode) or
              centred under each day column, below the X-axis (horizontal mode) */}
          {bubbleData.length > 0 && (() => {
            const allDates = layout.dayPositions.map(dp => dp.displayDate);

            if (scrollMode === 'vertical') {
              return (
                <div style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: `${layout.axisFixed - 5}px`,
                  height: "100%",
                  fontSize: "clamp(0.6rem, 2vw, 0.7rem)",
                  color: "var(--text-secondary, #666)",
                  fontWeight: "600",
                  pointerEvents: "none"
                }}>
                  {layout.dayPositions.map((dayPos, index) => {
                    const formattedDate = formatDisplayDate(dayPos.displayDate, allDates);

                    return (
                      <div
                        key={`date-label-${index}`}
                        style={{
                          position: "absolute",
                          top: `${dayPos.center}px`,
                          right: "4px",
                          transform: "translateY(-50%)",
                          whiteSpace: "nowrap",
                          textAlign: "right",
                          backgroundColor: "var(--bg-secondary, #fafbfc)",
                          padding: "2px 4px",
                          borderRadius: "3px",
                          border: "1px solid var(--border-color, #ddd)",
                          fontSize: "0.6rem",
                          maxWidth: `${layout.axisFixed - 10}px`,
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}
                      >
                        {formattedDate}
                      </div>
                    );
                  })}
                </div>
              );
            }

            // Horizontal mode: labels live under the X-axis, centred per day column
            return (
              <div style={{
                position: "absolute",
                top: `${layout.axisFixed}px`,
                left: 0,
                width: `${layout.svgWidth}px`,
                height: `${layout.svgHeight - layout.axisFixed}px`,
                fontSize: "clamp(0.6rem, 2vw, 0.7rem)",
                color: "var(--text-secondary, #666)",
                fontWeight: "600",
                pointerEvents: "none"
              }}>
                {layout.dayPositions.map((dayPos, index) => {
                  const formattedDate = formatDisplayDate(dayPos.displayDate, allDates);

                  return (
                    <div
                      key={`date-label-${index}`}
                      style={{
                        position: "absolute",
                        top: "4px",
                        left: `${dayPos.center}px`,
                        transform: "translateX(-50%)",
                        whiteSpace: "nowrap",
                        textAlign: "center",
                        backgroundColor: "var(--bg-secondary, #fafbfc)",
                        padding: "2px 4px",
                        borderRadius: "3px",
                        border: "1px solid var(--border-color, #ddd)",
                        fontSize: "0.6rem",
                        maxWidth: `${layout.daySize - 6}px`,
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                    >
                      {formattedDate}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
        </div>

        {/* Scroll indicator for more days */}
        {bubbleData.length > 0 && layout.dayPositions.length > DAYS_PER_VIEWPORT && (
          <div style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
            marginTop: "8px",
            fontSize: "0.75rem",
            color: "var(--text-secondary, #666)"
          }}>
            <span>Scroll for more</span>
            <span style={{ fontSize: "1rem" }}>{scrollMode === 'horizontal' ? '→' : '↓'}</span>
          </div>
        )}

        {/* Tooltip */}
        {tooltip.show && tooltip.game && (
          <div
            style={{
              position: "absolute",
              left: tooltip.x - 100,
              top: tooltip.y,
              transform: "translateX(-50%)",
              backgroundColor: "var(--text-primary, #333)",
              color: "white",
              padding: "8px 12px",
              borderRadius: "6px",
              fontSize: "clamp(0.75rem, 2vw, 0.875rem)",
              pointerEvents: "none",
              zIndex: 1000,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
              maxWidth: "200px",
              textAlign: "center",
              transition: "none"
            }}
          >
            <div style={{ fontWeight: "600", marginBottom: "4px" }}>
              {tooltip.game.gameName}
            </div>
            <div style={{ fontSize: "0.8rem", opacity: 0.9, marginBottom: "2px" }}>
              {tooltip.day}
            </div>
            <div style={{ fontSize: "0.8rem", opacity: 0.9 }}>
              {Math.round(tooltip.playtime)}min
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)"
          }}>
            <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>⏳</div>
            <div style={{ color: "var(--text-secondary, #666)", fontSize: "1rem" }}>
              Loading activity data...
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && bubbleData.length === 0 && (
          <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "var(--bg-secondary, #fafbfc)"
          }}>
            <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>📅</div>
            <div style={{ textAlign: "center", color: "var(--text-secondary, #666)" }}>
              <div style={{ fontSize: "clamp(0.875rem, 2vw, 1rem)" }}>
                No activity in this timeframe
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityBubbleChart;
